import { merge } from 'lodash';
import { buildApi } from '../../http-client/http-client';
import { toQueryParams } from '../../../utils/common-utils';
import withAuth from '../../auth-service/with-auth';
import * as Models from '../../../models';
import { MapperApi2_0 } from '../../../mappers';

export interface ChallengesResponse extends Models.Response.ResourcesList {
  resources: Models.Challenge[];
}

export enum ChallengeOwnerType {
  Myself = 'myself',
  MyTrainers = 'my_trainers',
}

type ChallengesRequest = {
  'filters[active]'?: boolean;
  'filters[opposition]'?: Models.OppositionId;
  'filters[academyCodes]'?: string;
  'filters[creatorTypes][]'?: Models.ChallengeCreatorType[];
  'filters[creators][]'?: ChallengeOwnerType[];
  'filters[hasAccessTimeLimitMinutes]'?: boolean;
  'filters[assignedToMe]'?: boolean;
  'filters[isSimulation]'?: boolean;
  'filters[tokens]'?: string;
  page?: number;
  pageSize?: number;
  orderDirection: Models.ChallengeOrderDirection;
  orderDateStart?: Models.ChallengeOrderDateStart;
};

export type ChallengesParams = {
  active?: boolean;
  oppositionId?: Models.OppositionId;
  academyCodes?: Models.ChallengeAcademyType[];
  creatorTypes?: Models.ChallengeCreatorType[];
  owners?: ChallengeOwnerType[];
  hasAccessTimeLimitMinutes?: boolean;
  isAssignedToMe?: boolean;
  page?: number;
  pageSize?: number;
  orderDirection?: Models.ChallengeOrderDirection;
  orderDateStart?: Models.ChallengeOrderDateStart;
  isSimulation?: boolean;
  challengeTokens?: Models.ChallengeToken[];
};

export const getChallenges = withAuth<ChallengesResponse, [ChallengesParams]>(
  async (token: string, params: ChallengesParams = {}): Promise<ChallengesResponse> => {
    const options = merge(
      {
        page: 1,
        orderDirection: Models.ChallengeOrderDirection.Asc,
        isSimulation: false,
      },
      params,
    );

    const request: ChallengesRequest = {
      'filters[active]': options.active,
      'filters[opposition]': options.oppositionId,
      'filters[academyCodes]': options.academyCodes?.join(','),
      'filters[creatorTypes][]': options.creatorTypes,
      'filters[creators][]': options.owners,
      'filters[hasAccessTimeLimitMinutes]': options.hasAccessTimeLimitMinutes,
      'filters[assignedToMe]': options.isAssignedToMe,
      'filters[isSimulation]': options.isSimulation,
      'filters[tokens]': options.challengeTokens?.join(','),
      page: options.page,
      pageSize: options.pageSize,
      orderDirection: options.orderDirection,
      orderDateStart: options.orderDateStart,
    };

    const query = toQueryParams(request);
    return buildApi(token)
      .get(`${process.env.API_BASE_URL}/api/v2.0/challenges?${query}`)
      .then((data: Models.Response.ResourcesList) => ({
        ...data,
        resources: MapperApi2_0.mapChallenges(data.resources),
      }));
  },
);
